// Batch-11 KL9 — mailnotificaties instelbaar per klant + per event.
//
// Alleen klant-gerichte trigger-keys zijn per klant instelbaar. Een ONTBREKENDE
// instelling = default (aan). De globale `EmailTrigger.enabled` blijft de
// kill-switch over alle klanten; deze laag verfijnt per klant.

import { z } from 'zod'
import { EMAIL_TRIGGER_KEY, type EmailTriggerKey } from './mail'

// De klant-gerichte events die per klant aan/uit kunnen. Bewust beperkt tot de
// trigger-keys die vandaag daadwerkelijk een klant-mail versturen, zodat de UI
// niets toont dat geen effect heeft.
export const CLIENT_CONFIGURABLE_EMAIL_TRIGGERS = [
  'WERKBON_NAAR_KLANT',
  'FACTUUR_NAAR_KLANT',
  'AUTOMATISERING_MELDING_KLANT',
  'KLANTPORTAAL_AANVRAAG_AFGEWEZEN',
  'KLANTPORTAAL_AANVRAAG_AFGEROND',
  // FO-304-G/I (batch-33) — klant-mails per relatie instelbaar (dagbundel +
  // wekelijkse reminder voor te-beoordelen items, contract-afloopmail).
  'KLANT_BEOORDELING_KLAARSTAAND',
  'KLANT_BEOORDELING_REMINDER',
  'KLANT_CONTRACT_AFLOOP',
  // KLANTPORTAAL_WELKOM bewust NIET instelbaar: een net-geregistreerde klant moet
  // altijd zijn inloggegevens krijgen, en de welkom-trigger geeft geen clientId mee
  // (de per-klant opt-out zou er toch niet op werken - dan liegt de UI).
] as const satisfies readonly EmailTriggerKey[]

export type ClientConfigurableEmailTrigger =
  (typeof CLIENT_CONFIGURABLE_EMAIL_TRIGGERS)[number]

// NL-labels per event (zichtbare copy voor de beheer-UI; geen merknaam-issues).
export const CLIENT_EMAIL_TRIGGER_LABELS: Record<ClientConfigurableEmailTrigger, string> = {
  WERKBON_NAAR_KLANT: 'Werkbon wacht op akkoord',
  FACTUUR_NAAR_KLANT: 'Factuur of factuurverzoek klaar',
  AUTOMATISERING_MELDING_KLANT: 'Automatische onderhoud-/planningsmelding',
  KLANTPORTAAL_AANVRAAG_AFGEWEZEN: 'Aanvraag afgewezen',
  KLANTPORTAAL_AANVRAAG_AFGEROND: 'Aanvraag afgerond',
  KLANT_BEOORDELING_KLAARSTAAND: 'Item klaar ter beoordeling (dagbundel)',
  KLANT_BEOORDELING_REMINDER: 'Wekelijkse herinnering openstaande beoordelingen',
  KLANT_CONTRACT_AFLOOP: 'Overeenkomst loopt af',
}

export function isClientConfigurableEmailTrigger(
  key: string,
): key is ClientConfigurableEmailTrigger {
  return (CLIENT_CONFIGURABLE_EMAIL_TRIGGERS as readonly string[]).includes(key)
}

export interface ClientEmailNotificationSettingDTO {
  triggerKey: ClientConfigurableEmailTrigger
  label: string
  /** Effectief: een ontbrekende rij valt terug op `true` (default aan). */
  enabled: boolean
  /** True wanneer dit de default is (geen opgeslagen rij). */
  isDefault: boolean
}

// Schema voor het bijwerken van één klant-event-instelling. `triggerKey` moet uit
// de configurable-set komen (anders weigeren); `enabled` is verplicht. De cast
// behoudt de literal-union (zodat `triggerKey` het smalle type infereert).
const clientConfigurableEnum = z.enum(
  CLIENT_CONFIGURABLE_EMAIL_TRIGGERS as unknown as readonly [
    ClientConfigurableEmailTrigger,
    ...ClientConfigurableEmailTrigger[],
  ],
)

export const UpdateClientEmailNotificationSchema = z.object({
  triggerKey: clientConfigurableEnum,
  enabled: z.boolean(),
})
export type UpdateClientEmailNotificationInput = z.infer<
  typeof UpdateClientEmailNotificationSchema
>

// Hulp: alle trigger-keys (voor cross-check elders).
export { EMAIL_TRIGGER_KEY }
